import { createSlice } from "@reduxjs/toolkit";
import { initialState } from "./initial-state";
import { addAddress } from "./thunks/add-address";
import { listAddresses } from "./thunks/list-addresses";
import { listGeoCatalog } from "./thunks/list-geo-catalog";
import { listContactLogs } from "./thunks/list-contact-logs";
import { listUndelivered } from "./thunks/list-undelivered";
import { loadCase } from "./thunks/load-case";
import { logContact } from "./thunks/log-contact";
import { reconfirm } from "./thunks/reconfirm";
import { reject } from "./thunks/reject";
import { reopen } from "./thunks/reopen";

const name = "undelivered";

export const slice = createSlice({
  name,
  initialState,
  reducers: {
    /** Closing the case pane drops what belonged to it, so a stale trail never
     *  shows under the next row the user clicks. */
    clearCase: (state) => {
      state.openCase = null;
      state.logs = [];
      state.addresses = [];
      state.actionError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Only the latest call lands: an earlier search can answer after a
      // later one (DMS-69).
      .addCase(listUndelivered.pending, (state, action) => {
        state.listRequestId = action.meta.requestId;
        state.isPending = true;
        state.isRejected = false;
        state.errorMessage = "";
      })
      .addCase(listUndelivered.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.queue = action.payload.items;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalItems = action.payload.totalItems;
        state.totalPages = action.payload.totalPages;
        state.statusCounts = action.payload.statusCounts;
        state.isPending = false;
      })
      .addCase(listUndelivered.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.isPending = false;
        state.isRejected = true;
        state.errorMessage = action.error.message ?? "";
      });

    builder
      .addCase(loadCase.pending, (state) => {
        state.isLoadingCase = true;
        state.openCase = null;
      })
      .addCase(loadCase.fulfilled, (state, action) => {
        state.openCase = action.payload;
        state.isLoadingCase = false;
      })
      .addCase(loadCase.rejected, (state) => {
        state.isLoadingCase = false;
      });

    builder.addCase(listAddresses.fulfilled, (state, action) => {
      state.addresses = action.payload;
    });

    builder.addCase(listGeoCatalog.fulfilled, (state, action) => {
      state.geoCatalog = action.payload;
    });

    builder
      .addCase(addAddress.pending, (state) => {
        state.isSavingAddress = true;
        state.actionError = "";
      })
      .addCase(addAddress.fulfilled, (state) => {
        state.isSavingAddress = false;
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.isSavingAddress = false;
        state.actionError = action.error.message ?? "";
      });

    builder
      .addCase(listContactLogs.pending, (state) => {
        state.isLoadingLogs = true;
      })
      .addCase(listContactLogs.fulfilled, (state, action) => {
        state.logs = action.payload;
        state.isLoadingLogs = false;
      })
      .addCase(listContactLogs.rejected, (state) => {
        state.isLoadingLogs = false;
      });

    builder
      .addCase(logContact.pending, (state) => {
        state.isLogging = true;
      })
      .addCase(logContact.fulfilled, (state, action) => {
        state.logs = [action.payload, ...state.logs];
        state.isLogging = false;
      })
      .addCase(logContact.rejected, (state, action) => {
        state.isLogging = false;
        state.actionError = action.error.message ?? "";
      });

    // The three state-moving actions share the acting flag: each takes the case
    // out of the tab it is in, so the screen reloads the queue after any of them.
    for (const thunk of [reconfirm, reject, reopen]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.isActing = true;
          state.actionError = "";
        })
        .addCase(thunk.fulfilled, (state) => {
          state.isActing = false;
          state.openCase = null;
          state.logs = [];
          state.addresses = [];
        })
        .addCase(thunk.rejected, (state, action) => {
          state.isActing = false;
          state.actionError = action.error.message ?? "";
        });
    }
  },
});

export const { clearCase } = slice.actions;
export const { reducer } = slice;
